import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import {
  useUpdateProject,
  useSyncProjectTools,
  solutionTypeToKey,
  solutionKeyToInt,
  type ProjectRow,
} from "@/hooks/useProjects";

const SOLUTION_OPTIONS = [
  { key: "content", label: "Creación de Contenido con IA" },
  { key: "vibecoding", label: "Vibecoding y Creación de Aplicaciones con IA" },
  { key: "assistant", label: "Asistente / Agente IA" },
  { key: "automation", label: "Automatización IA" },
  { key: "data", label: "Análisis de Datos con IA" },
];

const STATUS_OPTIONS = [
  { key: "ideation", label: "Ideación" },
  { key: "in_progress", label: "En progreso" },
  { key: "completed", label: "Completado" },
  { key: "paused", label: "Pausado" },
];

const FREQUENCY_OPTIONS = ["Diaria", "Semanal", "Quincenal", "Mensual", "Trimestral", "Semestral", "Anual"];

interface Props {
  project: ProjectRow;
  tools: string[];
  availableTools: Record<string, { name: string; icon: string }>;
  onCancel: () => void;
  onSaved?: () => void;
}

export function ProjectEditForm({ project, tools, availableTools, onCancel, onSaved }: Props) {
  const updateProject = useUpdateProject();
  const syncTools = useSyncProjectTools();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: project.name || "",
    solutionType: solutionTypeToKey(project.solution_type),
    status: project.status || "ideation",
    problem: project.problem || "",
    solutionDescription: project.solution_description || "",
    targetAudience: project.target_audience || "",
    dataUsed: project.data_used || "",
    kpiName: project.kpi_name || "",
    kpiCurrentValue: project.kpi_current_value || "",
    kpiTargetValue: project.kpi_target_value || "",
    measurementFrequency: project.measurement_frequency || "",
    isOrgVisible: !!project.is_org_visible,
    isPublic: !!project.is_public,
  });
  const [selectedTools, setSelectedTools] = useState<string[]>(tools);

  const set = (k: keyof typeof form, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const toggleTool = (id: string) =>
    setSelectedTools((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre del proyecto es obligatorio");
      return;
    }
    setSaving(true);
    try {
      await updateProject.mutateAsync({
        id: project.id,
        name: form.name.trim(),
        solution_type: solutionKeyToInt(form.solutionType),
        status: form.status as ProjectRow["status"],
        problem: form.problem,
        solution_description: form.solutionDescription,
        target_audience: form.targetAudience,
        data_used: form.dataUsed,
        kpi_name: form.kpiName,
        kpi_current_value: form.kpiCurrentValue,
        kpi_target_value: form.kpiTargetValue,
        measurement_frequency: form.measurementFrequency,
        is_org_visible: form.isOrgVisible,
        is_public: form.isPublic,
      } as any);

      const changedTools =
        selectedTools.length !== tools.length || selectedTools.some((t) => !tools.includes(t));
      if (changedTools) {
        await syncTools.mutateAsync({ projectId: project.id, toolNames: selectedTools });
      }

      toast.success("Proyecto actualizado");
      onSaved?.();
    } catch (err: any) {
      toast.error(err?.message || "No se pudo guardar el proyecto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5 md:col-span-2">
          <Label>Nombre del proyecto</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label>Tipo de solución</Label>
          <Select value={form.solutionType} onValueChange={(v) => set("solutionType", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOLUTION_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Estado</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 md:col-span-2">
          <Label>Público objetivo</Label>
          <Input value={form.targetAudience} onChange={(e) => set("targetAudience", e.target.value)} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Problema</Label>
        <Textarea rows={3} value={form.problem} onChange={(e) => set("problem", e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label>Descripción de la solución</Label>
        <Textarea rows={3} value={form.solutionDescription} onChange={(e) => set("solutionDescription", e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label>Datos usados</Label>
        <Textarea rows={2} value={form.dataUsed} onChange={(e) => set("dataUsed", e.target.value)} />
      </div>

      <div className="space-y-2">
        <Label>Herramientas</Label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(availableTools).map(([id, tool]) => {
            const active = selectedTools.includes(id);
            return (
              <button key={id} type="button" onClick={() => toggleTool(id)}>
                <Badge
                  variant={active ? "default" : "outline"}
                  className="gap-1.5 text-sm py-1 px-3 cursor-pointer"
                >
                  <span>{tool.icon}</span> {tool.name}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-xl border border-primary/20 bg-primary/5">
        <div className="space-y-1.5">
          <Label>KPI</Label>
          <Input value={form.kpiName} onChange={(e) => set("kpiName", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Valor actual</Label>
          <Input value={form.kpiCurrentValue} onChange={(e) => set("kpiCurrentValue", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Valor objetivo</Label>
          <Input value={form.kpiTargetValue} onChange={(e) => set("kpiTargetValue", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Frecuencia de medición</Label>
          <Select
            value={form.measurementFrequency || undefined}
            onValueChange={(v) => set("measurementFrequency", v)}
          >
            <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
            <SelectContent>
              {FREQUENCY_OPTIONS.map((f) => (
                <SelectItem key={f} value={f}>{f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-3">
          <Switch checked={form.isOrgVisible} onCheckedChange={(v) => set("isOrgVisible", v)} />
          <Label className="cursor-pointer">Visible para la organización</Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={form.isPublic} onCheckedChange={(v) => set("isPublic", v)} />
          <Label className="cursor-pointer">Visible públicamente</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          <X className="w-4 h-4 mr-1.5" /> Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} className="gradient-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
